import React, {useState, useCallback, useMemo} from 'react';
import {observer} from 'mobx-react';
import _ from 'lodash';
import {browserHistory} from 'react-router';
import InteractiveContext from 'shared/components/tables/interactive/components/InteractiveContext';
import Pagination from 'shared/components/tables/interactive/components/pagination/Pagination';
import {FilterRow} from 'shared/components/tables/interactive/components/FilterRow';
import AccordionTableContent from 'shared/components/tables/interactive/components/accordion_table/AccordionTableContent';
import Panel from 'shared/components/panels/Panel';
import Header from 'shared/components/tables/interactive/components/header/Header';
import {DomainStore} from 'shared/store';
import loadStateFromUrl from 'shared/components/tables/interactive/state/loadStateFromUrl';

const TableWrapper = observer(({wrapInPanel, children}) => {
  if (wrapInPanel) {
    return (
      <Panel>{children}</Panel>
    );
  }

  return (
    <div>{children}</div>
  );
});

const InteractiveAccordionTable = observer(function InteractiveAccordionTable({
  searchable = true,
  paginatable = true,
  proxy,
  folderProxy,
  saveStateToUrl,
  onMount,
  onRefresh,
  children,
  empty,
  initialFilter,
  initialSorting,
  hidePaginationInfo = false,
  fancyFiltersEnabled,
  onFilterUpdated = () => {},
  scopeOptions,
  columns = [],
  showLinks = true,
  localization = {},
  wrapInPanel = true,
  title = '',
  description,
  showAddButton = true,
  onAdd = () => {},
  tableActionButtons,
  horizontalScroll = false,
  ...rest
}) {
  const [isFoldersUpdating, setIsFoldersUpdating] = useState(true);
  const [folders, setFolders] = useState([]);
  const [currentPage, setCurrentPage] = useState(new URLSearchParams(location.search).get('p') || 1);
  const [filter, setFilter] = useState({});
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const initialExpandedIds = useMemo(() => {
    if (!saveStateToUrl) return [];

    const state = loadStateFromUrl();
    return state.expandedIds || [];
  }, []);

  const [expandedFolderIds, setExpandedFolderIds] = useState(initialExpandedIds);

  const updateExpandedIds = useCallback((newExpandedIds) => {
    setExpandedFolderIds(newExpandedIds);

    if (!saveStateToUrl) return;

    const location = browserHistory.getCurrentLocation();
    const query = {...location.query};

    if (newExpandedIds.length > 0) {
      query.ex = newExpandedIds.join(',');
    } else {
      delete query.ex;
    }

    browserHistory.replace({...location, query});
  }, [saveStateToUrl]);

  const handleFolderExpanded = useCallback((folderId) => {
    const newExpandedIds = [...expandedFolderIds, folderId];
    updateExpandedIds(newExpandedIds);
  }, [expandedFolderIds, updateExpandedIds]);

  const handleFolderCollapsed = useCallback((folderId) => {
    const newExpandedIds = expandedFolderIds.filter(id => id !== folderId);
    updateExpandedIds(newExpandedIds);
  }, [expandedFolderIds, updateExpandedIds]);

  const fetchFolders = useCallback(async (filterObj) => {
    if (!folderProxy) return;

    const {endpoint, model, modelType} = folderProxy;
    const store = new DomainStore();
    const url = typeof endpoint === 'function' ? endpoint(filterObj) : endpoint;
    await store._compose([url]);
    setFolders(store._getAll(modelType, model));
  }, [folderProxy]);

  const handleOnMount = useCallback(async (agent) => {
    setIsEmpty(agent.isEmpty);

    await fetchFolders(agent.filter.toJSON());

    onMount && onMount(
      {
        ...agent,
        refreshFolders: () => fetchFolders(agent.filter.toJSON())
      }
    );
    setIsFoldersUpdating(false);
    setIsLoading(false);
  }, [onMount, fetchFolders]);

  const handleFilterUpdated = useCallback(async (filterValue) => {
    setIsFoldersUpdating(true);
    setCurrentPage(1);
    setFilter(filterValue.toJSON());
    onFilterUpdated(filterValue);
    await fetchFolders(filterValue.toJSON());
    setIsFoldersUpdating(false);
  }, [onFilterUpdated, fetchFolders]);

  const handlePaginationUpdated = useCallback(({currentPage: newPage}) => {
    setCurrentPage(newPage);
  }, []);

  const {emptyStateMessage} = localization;

  if (!horizontalScroll) {
    if (showLinks && _.sumBy(columns, 'width') !== 11) {
      throw new Error('Columns must add up to a width of 11 when showLinks is set to true. (1 column is taken by the dropdown with edit/delete links.)');
    }
    if (!showLinks && _.sumBy(columns, 'width') !== 12) {
      throw new Error('Columns must add up to a width of 12.');
    }
  }

  if (isEmpty && _.isFunction(empty)) {
    const EmptyState = empty;
    return <EmptyState {...rest} />;
  }

  const foldersToShow = currentPage === 1 && !filter.search ? folders : [];

  return (
    <TableWrapper wrapInPanel={wrapInPanel}>
      <Header
        showAddButton={showAddButton}
        onAdd={onAdd}
        actionButtons={tableActionButtons}
        localization={_.get(localization, 'interactive')}
        description={description}
        title={title}
        loading={isLoading}
      />
      <InteractiveContext
        proxy={proxy}
        onMount={handleOnMount}
        onRefresh={onRefresh}
        saveStateToUrl={saveStateToUrl}
        empty={empty}
        initialFilter={initialFilter}
        initialSorting={initialSorting}
        onFilterUpdated={handleFilterUpdated}
        onPaginationUpdated={handlePaginationUpdated}
      >
        <FilterRow className='AccordionTableFilters' searchable={searchable} fancyFiltersEnabled={fancyFiltersEnabled} scopeOptions={scopeOptions}>
          {children}
        </FilterRow>
        <AccordionTableContent
          columns={columns}
          emptyStateMessage={emptyStateMessage}
          showLinks={showLinks}
          localization={localization}
          folders={foldersToShow}
          isFoldersUpdating={isFoldersUpdating}
          horizontalScroll={horizontalScroll}
          expandedFolderIds={expandedFolderIds}
          onFolderExpanded={handleFolderExpanded}
          onFolderCollapsed={handleFolderCollapsed}
          {...rest}
        />
        {paginatable && <Pagination hidePaginationInfo={hidePaginationInfo} />}
      </InteractiveContext>
    </TableWrapper>
  );
});

export default InteractiveAccordionTable;
